/**
 * Thread Selector — Horizontal bar of life thread tabs.
 * Colored dot + name per active thread, "+" to create new.
 * Double-click any thread name to rename it inline.
 */

import { useState, useRef, useEffect } from 'react';
import { Plus, X, Trash2, Eraser, ArrowUpCircle } from 'lucide-react';
import { createThread, updateThread } from '../lib/api';
import ConfirmDialog from './ConfirmDialog';

const COLOR_PALETTE = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

export default function ThreadSelector({ threads, activeThreadId, onSelectThread, onThreadsChange, onDeleteThread, onClearThread, spaceId }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(null);
  const [limitMessage, setLimitMessage] = useState(null);
  const editRef = useRef(null);

  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editingId]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || saving) return;

    setSaving(true);
    try {
      const usedColors = new Set(threads.map(t => t.color));
      const color = COLOR_PALETTE.find(c => !usedColors.has(c)) || COLOR_PALETTE[threads.length % COLOR_PALETTE.length];

      const res = await createThread({ name, color, space_id: spaceId });
      if (res.limit_type) {
        setLimitMessage(res.upgrade_message || res.error);
        setCreating(false);
        setNewName('');
        setTimeout(() => setLimitMessage(null), 5000);
        return;
      }
      if (res.thread) {
        setLimitMessage(null);
        onThreadsChange([...threads, res.thread]);
        onSelectThread(res.thread.id);
        setCreating(false);
        setNewName('');
      }
    } catch (err) {
      console.error('Failed to create thread:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (threadId) => {
    const name = editName.trim();
    if (!name || saving) { setEditingId(null); return; }

    const thread = threads.find(t => t.id === threadId);
    if (thread && thread.name === name) { setEditingId(null); return; }

    setSaving(true);
    try {
      const res = await updateThread(threadId, { name });
      if (res.thread) {
        onThreadsChange(threads.map(t => t.id === threadId ? { ...t, name: res.thread.name } : t));
      }
    } catch (err) {
      console.error('Failed to rename thread:', err);
    } finally {
      setSaving(false);
      setEditingId(null);
    }
  };

  const handleCreateKeyDown = (e) => {
    if (e.key === 'Enter') handleCreate();
    if (e.key === 'Escape') { setCreating(false); setNewName(''); }
  };

  const handleEditKeyDown = (e, threadId) => {
    if (e.key === 'Enter') handleRename(threadId);
    if (e.key === 'Escape') setEditingId(null);
  };

  return (
    <div className="border-b border-slate-800 px-safe-4 py-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-fade-r">
      {threads.filter(t => t.status === 'active').map(thread => (
        editingId === thread.id ? (
          <div key={thread.id} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: thread.color || '#3B82F6' }}
            />
            <input
              ref={editRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => handleEditKeyDown(e, thread.id)}
              onBlur={() => handleRename(thread.id)}
              maxLength={100}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200 w-28 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        ) : (
          <div
            key={thread.id}
            className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${
              thread.id === activeThreadId
                ? 'bg-slate-700 text-slate-200'
                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
            }`}
          >
            <button
              onClick={() => onSelectThread(thread.id)}
              onDoubleClick={(e) => {
                e.preventDefault();
                setEditingId(thread.id);
                setEditName(thread.name);
              }}
              className="flex items-center gap-1.5"
              title="Double-click to rename"
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: thread.color || '#3B82F6' }}
              />
              {thread.name}
              {thread.pending_assignments > 0 && (
                <span className="bg-amber-900/40 text-amber-400 text-[11px] px-1.5 py-0.5 rounded-full leading-none">
                  {thread.pending_assignments}
                </span>
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setClearConfirm(thread);
              }}
              aria-label={`Clear history for ${thread.name}`}
              className="tap-target-sm sm:opacity-0 sm:group-hover:opacity-100 text-slate-500 hover:text-amber-400 transition-all ml-0.5 -my-1.5 rounded hover:bg-slate-700/50"
              title="Clear history"
            >
              <Eraser size={13} />
            </button>
            {!thread.is_default && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(thread);
                }}
                aria-label={`Delete ${thread.name}`}
                className="tap-target-sm sm:opacity-0 sm:group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all -my-1.5 rounded hover:bg-slate-700/50"
                title="Delete thread"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        )
      ))}

      {creating ? (
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleCreateKeyDown}
            placeholder="Thread name..."
            maxLength={100}
            autoFocus
            className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200 placeholder-slate-500 w-32 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={handleCreate}
            disabled={!newName.trim() || saving}
            className="text-xs text-blue-400 hover:text-blue-300 disabled:text-slate-600 px-1"
          >
            Create
          </button>
          <button
            onClick={() => { setCreating(false); setNewName(''); }}
            className="text-slate-500 hover:text-slate-300"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setCreating(true)}
          aria-label="New thread"
          className="tap-target-sm flex-shrink-0 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-colors"
          title="New thread"
        >
          <Plus size={16} />
        </button>
      )}

      {limitMessage && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-amber-400">
          <ArrowUpCircle size={12} />
          {limitMessage}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteConfirm}
        title={deleteConfirm ? `Delete '${deleteConfirm.name}'?` : ''}
        message="This will permanently delete all conversations, assignments, and progress in this thread. This cannot be undone."
        confirmLabel="Delete Thread"
        onConfirm={() => {
          if (deleteConfirm && onDeleteThread) {
            onDeleteThread(deleteConfirm.id);
          }
          setDeleteConfirm(null);
        }}
        onCancel={() => setDeleteConfirm(null)}
      />

      <ConfirmDialog
        open={!!clearConfirm}
        title={clearConfirm ? `Clear history for '${clearConfirm.name}'?` : ''}
        message="This will permanently delete all conversations, assignments, and progress in this thread. The thread itself will remain. This cannot be undone."
        confirmLabel="Clear History"
        onConfirm={() => {
          if (clearConfirm && onClearThread) {
            onClearThread(clearConfirm.id);
          }
          setClearConfirm(null);
        }}
        onCancel={() => setClearConfirm(null)}
      />
    </div>
  );
}
